import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

/**
 * Per-collector poll status for one (gateway, device, collector)
 * combination. Mirrors the gateway-side `CollectorStatus` enum.
 *
 * - `healthy`   — last execute succeeded
 * - `degraded`  — last execute failed, below the trip threshold
 * - `failing`   — at/above threshold; alertable
 * - `cooldown`  — circuit breaker has paused polling on purpose
 *
 * `lastErrorClass` is set only when status is non-healthy and the
 * gateway was able to classify the error:
 *   - `auth`    — credential / lockout / login-flow incompatibility
 *   - `network` — connect refused, timeout, unreachable
 *   - `parse`   — connected fine but response wasn't parseable
 *   - `unknown` — couldn't classify
 */
export type CollectorStatus = 'healthy' | 'degraded' | 'failing' | 'cooldown';
export type CollectorErrorClass = 'auth' | 'network' | 'parse' | 'unknown';

export interface CollectorHealth {
  gatewayId: string;
  /** Name of the reporting gateway (null if never named). */
  gatewayName: string | null;
  /** Reporting gateway's liveness: 'online' | 'offline' | 'unknown'. */
  gatewayStatus: string;
  collectorName: string;
  status: CollectorStatus;
  consecutiveFailures: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastErrorClass: CollectorErrorClass | null;
  lastErrorMessage: string | null;
  statusChangedAt: string;
  /** When this collector is next expected to run: last-run + interval
   *  normally, or the cooldown end while paused. Null on older gateways. */
  nextCollectionAt: string | null;
  /**
   * What the last run cost the *device*. Bytes describe the wire; these
   * describe the work — a PDU is an agent parse/dispatch plus a subagent
   * round, and a session open is a process spawn on the device.
   *
   * All null on a gateway older than the reporting change. That is genuinely
   * "not measured", not "zero", so the UI omits the line entirely rather than
   * rendering a 0 it never observed.
   */
  lastDurationMs: number | null;
  ewmaDurationMs: number | null;
  lastPdus: number | null;
  lastSessionOpens: number | null;
  /**
   * The interval this collector is actually scheduled at on this device, after
   * per-device tuning — not its compiled-in default. With `lastDurationMs`
   * this gives duty cycle: how much of each interval the device spends
   * answering us.
   */
  effectiveIntervalSeconds: number | null;
  /**
   * Why the cadence is what it is. Null means normal.
   *
   * - `structural_deferred`  — low-volatility work postponed under load
   * - `counters_reduced`     — counter cadence stretched; a real, visible
   *                            reduction in what the user sees
   * - `over_budget_at_floor` — this collector costs more than its duty budget
   *                            allows at the cadence its data requires. The
   *                            floor was held and the extra load accepted, so
   *                            no data was lost; the device is undersized for
   *                            what we ask, or its walk is still unscoped.
   *
   * Surfaced rather than swallowed: the design commits to never silently
   * trading away visibility, which means never silently keeping it either.
   */
  cadenceState: CollectorCadenceState | null;
}

export type CollectorCadenceState =
  | 'normal'
  | 'structural_deferred'
  | 'counters_reduced'
  | 'over_budget_at_floor';

/**
 * Per-method connect stats. Populated by the gateway's
 * `ConnectionManager` on every connect attempt — both successes
 * (latency rolled into `avgLatencyMs`) and failures
 * (`failureReason` carries the verbatim error string).
 */
export interface ConnectionMethodHealth {
  gatewayId: string;
  /** Name of the reporting gateway (null if never named). */
  gatewayName: string | null;
  /** Reporting gateway's liveness: 'online' | 'offline' | 'unknown'. */
  gatewayStatus: string;
  deviceConnectionMethodId: string;
  sessionType: 'ssh' | 'telnet' | 'http' | 'https' | 'console' | 'netconf';
  host: string | null;
  port: number | null;
  priority: number;
  enabled: boolean;
  lastSuccess: string | null;
  lastFailure: string | null;
  failureReason: string | null;
  avgLatencyMs: number | null;
  successCount: number;
  failureCount: number;
  updatedAt: string;
}

export interface DeviceHealth {
  deviceId: string;
  collectors: CollectorHealth[];
  connectionMethods: ConnectionMethodHealth[];
}

const backendApi = config.backendApi;

const fetchDeviceHealth = async (deviceId: string): Promise<DeviceHealth> => {
  const response = await axios.get<DeviceHealth>(
    `${backendApi}/api/devices/${deviceId}/health`,
    { withCredentials: true },
  );
  return response.data;
};

/**
 * Read both collector and connection-method health for a device.
 * Backed by a single backend round-trip — the two signals live in
 * separate tables but ship together to avoid a React-Query waterfall
 * on the device-detail page.
 *
 * Refetches every 30s while the tab is visible so the UI tracks the
 * gateway's 60s upload cadence with a bit of headroom for the
 * upload to land before the user sees stale data.
 */
export function useDeviceHealth(deviceId: string | null | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery({
    queryKey: ['device-health', deviceId],
    queryFn: () => {
      if (!deviceId) throw new Error('deviceId required');
      return fetchDeviceHealth(deviceId);
    },
    enabled: !!deviceId && contextReady,
    refetchInterval: 30_000,
  });
}

export interface TriggerCollectorResponse {
  success: boolean;
  status: string;
  message: string;
  durationMs: number;
}

/**
 * Manually run a single collector now, bypassing its schedule and any
 * circuit-breaker cooldown ("Run now" on the Health tab). On completion the
 * device-health query is invalidated so the collector card reflects the
 * fresh status.
 */
export function useTriggerCollector(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (collectorName: string) => {
      const response = await axios.post<TriggerCollectorResponse>(
        `${backendApi}/api/devices/${deviceId}/collectors/${encodeURIComponent(collectorName)}/trigger`,
        {},
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: (data, collectorName) => {
      if (data.success) {
        toast.success(`${collectorName}: collected`);
      } else {
        toast.warning(`${collectorName}: ${data.message || data.status || 'no data'}`);
      }
      void queryClient.invalidateQueries({ queryKey: ['device-health', deviceId] });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'unknown error';
      toast.error(`Collection failed: ${message}`);
    },
  });
}
